<template>
  <b-card-code
    title="Modifier un livreur"
    aria-label="Formulaire de modification du livreur"
  >
    <b-overlay
      :show="isLoading"
      rounded="lg"
      spinner-variant="primary"
      aria-live="polite"
      aria-busy="isLoading"
    >
      <b-card v-if="delivery_man">
        <!-- Info alert for user guidance -->
        <b-alert
          variant="info"
          show
          class="mb-3"
          aria-live="polite"
        >
          <feather-icon
            icon="InfoIcon"
            size="16"
            class="mr-1"
            aria-hidden="true"
          />
          <span>Modifiez les informations du livreur pour garantir la cohérence de votre flotte logistique. Tous les champs marqués d'un astérisque (<span aria-hidden="true">*</span>) sont obligatoires.</span>
        </b-alert>
        <!-- Header with info summary -->
        <div
          class="driver-header mb-3"
          aria-label="Résumé du livreur"
        >
          <div class="d-flex align-items-center">
            <b-avatar
              :text="getInitials(delivery_man.username)"
              variant="light-success"
              size="lg"
              :aria-label="'Avatar du livreur ' + delivery_man.username"
            />
            <div class="ml-2">
              <h4
                class="mb-0"
                aria-label="Nom du livreur"
              >
                {{ delivery_man.username }}
              </h4>
              <div class="d-flex align-items-center">
                <feather-icon
                  icon="MailIcon"
                  size="12"
                  class="text-muted mr-1"
                  aria-hidden="true"
                />
                <span
                  class="text-muted"
                  aria-label="Adresse e-mail professionnelle"
                >{{ delivery_man.email }}</span>
              </div>
            </div>
          </div>
        </div>

        <b-form
          aria-label="Formulaire de modification du livreur"
          @submit.prevent="validateAndSubmit"
        >
          <!-- Informations personnelles du livreur -->
          <div
            class="form-section mb-4"
            aria-label="Section informations personnelles du livreur"
          >
            <h5 class="section-title">
              <feather-icon
                icon="UserIcon"
                class="mr-1"
                size="18"
                aria-hidden="true"
              />
              Informations personnelles du livreur
            </h5>

            <b-row>
              <b-col md="6">
                <b-form-group
                  label="Nom complet du livreur *"
                  label-for="username"
                  description="Saisissez le nom complet tel qu'il figure sur les documents officiels. Champ obligatoire."
                >
                  <validation-provider
                    #default="{ errors }"
                    name="nom"
                    rules="required"
                  >
                    <b-form-input
                      id="username"
                      v-model="delivery_man.username"
                      :state="errors.length > 0 ? false : null"
                      placeholder="Entrez le nom complet du livreur"
                      aria-label="Nom complet du livreur"
                      aria-required="true"
                      aria-describedby="username-desc"
                    />
                    <b-form-invalid-feedback aria-live="polite">
                      {{ errors[0] }}
                    </b-form-invalid-feedback>
                    <small
                      id="username-desc"
                      class="sr-only"
                    >Ce champ est requis pour l'identification du livreur dans la flotte logistique.</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <!-- Téléphone secondaire -->
              <b-col md="6">
                <b-form-group
                  label="Téléphone secondaire (optionnel)"
                  label-for="phone2"
                  description="Saisissez le numéro de téléphone secondaire du livreur (8 chiffres, uniquement des chiffres)."
                >
                  <b-form-input
                    id="phone2"
                    v-model="phone2"
                    placeholder="Entrez le numéro de téléphone secondaire"
                    maxlength="8"
                    aria-label="Numéro de téléphone secondaire du livreur"
                  />
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group
                  label="Numéro de téléphone professionnel *"
                  label-for="phone"
                  description="Saisissez le numéro de téléphone du livreur (8 chiffres, uniquement des chiffres). Champ obligatoire."
                >
                  <validation-provider
                    #default="{ errors }"
                    name="Téléphone"
                    rules="required|integer|length:8"
                  >
                    <b-input-group>
                      <b-form-input
                        id="phone"
                        v-model="delivery_man.phone"
                        :state="errors.length > 0 ? false : null"
                        placeholder="Entrez le numéro de téléphone professionnel"
                        aria-label="Numéro de téléphone professionnel"
                        aria-required="true"
                        aria-describedby="phone-desc"
                      />
                      <b-input-group-append is-text>
                        <feather-icon
                          icon="PhoneIcon"
                          size="16"
                          aria-hidden="true"
                        />
                      </b-input-group-append>
                    </b-input-group>
                    <b-form-invalid-feedback aria-live="polite">
                      {{ errors[0] }}
                    </b-form-invalid-feedback>
                    <small
                      id="phone-desc"
                      class="sr-only"
                    >Ce champ est requis pour contacter le livreur en cas d'urgence logistique.</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <b-col md="6">
                <b-form-group
                  label="Adresse e-mail professionnelle *"
                  label-for="email"
                  description="Saisissez l'adresse e-mail professionnelle du livreur. Champ obligatoire."
                >
                  <validation-provider
                    #default="{ errors }"
                    name="Email"
                    rules="required|email"
                  >
                    <b-input-group>
                      <b-form-input
                        id="email"
                        v-model="delivery_man.email"
                        :state="errors.length > 0 ? false : null"
                        placeholder="Entrez l'adresse e-mail professionnelle"
                        aria-label="Adresse e-mail professionnelle"
                        aria-required="true"
                        aria-describedby="email-desc"
                      />
                      <b-input-group-append is-text>
                        <feather-icon
                          icon="MailIcon"
                          size="16"
                          aria-hidden="true"
                        />
                      </b-input-group-append>
                    </b-input-group>
                    <b-form-invalid-feedback aria-live="polite">
                      {{ errors[0] }}
                    </b-form-invalid-feedback>
                    <small
                      id="email-desc"
                      class="sr-only"
                    >Ce champ est requis pour la communication officielle avec le livreur.</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <b-col md="6">
                <b-form-group
                  label="Adresse postale"
                  label-for="address"
                  description="Indiquez l'adresse postale du livreur (optionnel)."
                >
                  <validation-provider
                    #default="{ errors }"
                    name="Adresse"
                  >
                    <b-input-group>
                      <b-form-textarea
                        id="address"
                        v-model="delivery_man.address"
                        :state="errors.length > 0 ? false : null"
                        placeholder="Entrez l'adresse postale du livreur"
                        aria-label="Adresse postale du livreur"
                        aria-describedby="address-desc"
                        rows="3"
                      />
                      <b-input-group-append is-text>
                        <feather-icon
                          icon="MapPinIcon"
                          size="16"
                          aria-hidden="true"
                        />
                      </b-input-group-append>
                    </b-input-group>
                    <b-form-invalid-feedback aria-live="polite">
                      {{ errors[0] }}
                    </b-form-invalid-feedback>
                    <small
                      id="address-desc"
                      class="sr-only"
                    >Ce champ est optionnel et permet de localiser le livreur pour la logistique terrain.</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <b-col md="6">
                <b-form-group
                  label="Dépôt logistique *"
                  label-for="repository"
                  description="Sélectionnez le dépôt logistique principal du livreur. Champ obligatoire."
                >
                  <v-select
                    id="repository"
                    v-model="selectedRepository"
                    :options="repositories"
                    label="name"
                    :reduce="repo => repo.id"
                    placeholder="Sélectionnez un dépôt logistique"
                    class="enhanced-select"
                    aria-label="Dépôt logistique principal du livreur"
                    aria-required="true"
                    aria-describedby="repository-desc"
                  />
                  <small
                    id="repository-desc"
                    class="sr-only"
                  >Ce champ est requis pour l'affectation logistique du livreur.</small>
                </b-form-group>
              </b-col>

              <!-- Car -->
              <b-col md="6">
                <b-form-group
                  label="Véhicule attribué"
                  label-for="car"
                  description="Sélectionnez le véhicule attribué au livreur (optionnel)."
                >
                  <v-select
                    id="car"
                    v-model="selectedCar"
                    :options="cars"
                    label="name"
                    :reduce="car => car.id"
                    placeholder="Sélectionnez un véhicule attribué (optionnel)"
                    class="enhanced-select"
                    clearable
                    aria-label="Véhicule attribué au livreur"
                    aria-describedby="car-desc"
                  />
                  <small
                    id="car-desc"
                    class="sr-only"
                  >Ce champ est optionnel et permet d'associer un véhicule à ce livreur.</small>
                </b-form-group>
              </b-col>

              <!-- Owns Vehicle -->
              <b-col md="6">
                <b-form-group
                  label="Propriétaire du Véhicule"
                  label-for="owns_vehicle"
                  description="Le livreur possède-t-il son propre véhicule ?"
                >
                  <b-form-checkbox
                    id="owns_vehicle"
                    v-model="delivery_man.owns_vehicle"
                    switch
                  >
                    {{ delivery_man.owns_vehicle ? 'Oui' : 'Non' }}
                  </b-form-checkbox>
                </b-form-group>
              </b-col>
            </b-row>
          </div>

          <!-- Payment Information Section -->
          <div
            class="form-section mb-4"
            aria-label="Section informations de paiement"
          >
            <h5 class="section-title">
              <feather-icon
                icon="DollarSignIcon"
                class="mr-1"
                size="18"
                aria-hidden="true"
              />
              Informations de Paiement
            </h5>

            <b-row>
              <!-- Payment Type -->
              <b-col md="6">
                <b-form-group
                  label="Type de Paiement"
                  label-for="pay_type"
                  description="Sélectionnez le mode de rémunération du livreur."
                >
                  <v-select
                    id="pay_type"
                    v-model="delivery_man.pay_type"
                    :options="paymentTypeOptions"
                    label="label"
                    :reduce="option => option.value"
                    placeholder="Sélectionnez un type de paiement (optionnel)"
                    clearable
                    class="enhanced-select"
                  />
                </b-form-group>
              </b-col>

              <!-- Compensation Amount -->
              <b-col md="6">
                <b-form-group
                  :label="compensationLabel"
                  label-for="compensation"
                  :description="compensationDescription"
                >
                  <b-form-input
                    id="compensation"
                    v-model.number="delivery_man.compensation"
                    type="number"
                    step="0.001"
                    min="0"
                    placeholder="0.000"
                  />
                </b-form-group>
              </b-col>
            </b-row>
          </div>

          <!-- Form Actions -->
          <div class="d-flex justify-content-between mt-3">
            <b-button
              variant="outline-secondary"
              :to="{ name: 'delivery-man-list' }"
              aria-label="Annuler et retourner à la liste des livreurs"
            >
              <feather-icon
                icon="XIcon"
                class="mr-1"
                aria-hidden="true"
              />
              Annuler
            </b-button>

            <b-button
              type="submit"
              variant="primary"
              :disabled="isSubmitting"
              aria-label="Enregistrer les modifications du livreur"
            >
              <b-spinner
                v-if="isSubmitting"
                small
                class="mr-1"
                aria-hidden="true"
              />
              <feather-icon
                v-else
                icon="SaveIcon"
                class="mr-1"
                aria-hidden="true"
              />
              Enregistrer
            </b-button>
          </div>
        </b-form>
      </b-card>
    </b-overlay>
  </b-card-code>
</template>

<script>
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import BCardCode from '@core/components/b-card-code'
import vSelect from 'vue-select'
import { ValidationProvider } from 'vee-validate'
import {
  BFormInput, BFormGroup, BForm, BRow, BCol, BButton, BAvatar,
  BInputGroup, BInputGroupAppend, BCard, BOverlay, BFormTextarea, BFormInvalidFeedback,
  BFormCheckbox, BSpinner, BAlert,
} from 'bootstrap-vue'
import { required } from '@validations'
import storeAuth from '@/store/store'

export default {
  components: {
    BCardCode,
    ValidationProvider,
    BFormInput,
    BFormGroup,
    BForm,
    BRow,
    BCol,
    BButton,
    BInputGroup,
    BInputGroupAppend,
    BCard,
    BOverlay,
    BAvatar,
    BFormTextarea,
    BFormInvalidFeedback,
    BFormCheckbox,
    BSpinner,
    BAlert,
    vSelect,
  },

  data() {
    return {
      repositories: [],
      cars: [],
      delivery_man: null,
      selectedRepository: null,
      selectedCar: null,
      required,
      isLoading: false,
      isSubmitting: false,
      user: {},
      stats: {
        deliveries: 0,
        successRate: 0,
        avgDeliveryTime: 0,
        rating: 0,
      },
      phone2: '',
      paymentTypeOptions: [
        { value: 'salary', label: 'Salaire' },
        { value: 'commission', label: 'Commission (Par Colis)' },
      ],
    }
  },

  computed: {
    compensationLabel() {
      if (!this.delivery_man) return 'Montant (DT)'

      if (this.delivery_man.pay_type === 'salary') {
        return 'Salaire Mensuel (DT)'
      }
      if (this.delivery_man.pay_type === 'commission') {
        return 'Taux par Colis (DT)'
      }
      return 'Montant (DT)'
    },
    compensationDescription() {
      if (!this.delivery_man) return 'Montant de la rémunération.'

      if (this.delivery_man.pay_type === 'salary') {
        return 'Salaire mensuel fixe du livreur.'
      }
      if (this.delivery_man.pay_type === 'commission') {
        return 'Montant payé par colis livré.'
      }
      return 'Montant de la rémunération.'
    },
  },

  created() {
    this.user = storeAuth.state.user
    this.getRepositories()
    this.getCars()
    this.getDeliveryMan()
  },

  methods: {
    async getRepositories() {
      try {
        const { data } = await axios.get('/api/repositories/')
        this.repositories = data
      } catch (error) {
        this.showToast('danger', 'Erreur lors du chargement des dépôts')
      }
    },

    async getCars() {
      try {
        const { data } = await axios.get('/api/cars/')
        this.cars = (data.results || data).map(car => ({
          ...car,
          name: `${car.brand} ${car.model || ''}`.trim(),
        }))
      } catch (error) {
        this.showToast('danger', 'Erreur lors du chargement des véhicules')
      }
    },

    async getDeliveryMan() {
      this.isLoading = true
      try {
        const { id } = this.$route.params
        const { data } = await axios.get(`/api/auth/users/delivery-men/${id}/`)
        this.delivery_man = data
        // Populate phone2 field with existing value
        this.phone2 = this.delivery_man.phone2 || ''

        // Populate payment info from payment_info object if available
        // Use $set to ensure reactivity
        if (this.delivery_man.payment_info) {
          this.$set(this.delivery_man, 'pay_type', this.delivery_man.payment_info.pay_type)
          this.$set(this.delivery_man, 'compensation', this.delivery_man.payment_info.compensation)
          this.$set(this.delivery_man, 'owns_vehicle', this.delivery_man.payment_info.owns_vehicle)
        } else {
          // Initialize with null values if no payment info exists
          this.$set(this.delivery_man, 'pay_type', null)
          this.$set(this.delivery_man, 'compensation', null)
          this.$set(this.delivery_man, 'owns_vehicle', false)
        }

        if (this.delivery_man.repository) {
          this.selectedRepository = this.delivery_man.repository
        }

        if (this.delivery_man.car) {
          this.selectedCar = this.delivery_man.car
        }
      } catch (error) {
        this.showToast('danger', 'Erreur lors du chargement des données du livreur')
      } finally {
        this.isLoading = false
      }
    },

    async validateAndSubmit() {
      const observer = this.$refs.simpleRules
      if (!observer) {
        await this.editDeliveryMan()
        return
      }

      const success = await observer.validate()
      if (success) {
        await this.editDeliveryMan()
      } else {
        this.showToast('warning', 'Veuillez corriger les erreurs dans le formulaire')
      }
    },

    async editDeliveryMan() {
      this.isSubmitting = true

      try {
        // Send all editable fields from the form
        const payload = {
          username: this.delivery_man.username,
          email: this.delivery_man.email,
          phone: this.delivery_man.phone,
          phone2: this.phone2,
          address: this.delivery_man.address,
          repository: this.selectedRepository,
          car: this.selectedCar,
          pay_type: this.delivery_man.pay_type,
          compensation: this.delivery_man.compensation,
          owns_vehicle: this.delivery_man.owns_vehicle || false,
        }

        await axios.put(
          `/api/auth/users/delivery-men/update/${this.delivery_man.id}/`,
          payload,
        )

        this.showToast('success', 'Livreur modifié avec succès')
        this.$router.push({ name: 'delivery-man-list' })
      } catch (error) {
        this.showToast('danger', error.response?.data?.message || 'Erreur lors de la modification du livreur')
      } finally {
        this.isSubmitting = false
      }
    },

    showToast(variant, text) {
      let title
      let icon

      if (variant === 'success') {
        title = 'Succès'
        icon = 'CheckCircleIcon'
      } else if (variant === 'warning') {
        title = 'Attention'
        icon = 'AlertTriangleIcon'
      } else {
        title = 'Erreur'
        icon = 'XCircleIcon'
      }

      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon,
          text,
          variant,
        },
      })
    },

    getInitials(name) {
      if (!name) return '??'
      return name
        .split(' ')
        .map(word => word[0])
        .join('')
        .toUpperCase()
    },

    truncate(text, length) {
      if (!text) return ''
      return text.length > length ? `${text.substring(0, length)}...` : text
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@core/scss/vue/libs/vue-select.scss";

.cursor-pointer {
  cursor: pointer;
}

.driver-header {
  padding: 1rem;
  border-radius: 0.5rem;
  background-color: #f8f8f8;
}

.form-section {
  padding: 1.5rem;
  background-color: #f8f9fa;
  border-radius: 0.5rem;

  .section-title {
    display: flex;
    align-items: center;
    margin-bottom: 1.5rem;
    color: #5e5873;
    font-weight: 500;
  }
}

.form-actions {
  margin-top: 2rem;
}

// Statistics styles
.statistics-row {
  .stat-card {
    display: flex;
    align-items: center;
    padding: 0.75rem;
    border-radius: 0.5rem;
    background-color: white;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    height: 100%;

    .stat-icon {
      width: 46px;
      height: 46px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-right: 0.75rem;
    }

    .stat-details {
      flex-grow: 1;

      .stat-value {
        margin-bottom: 0.25rem;
        font-size: 1.25rem;
        font-weight: 600;
      }

      small {
        color: #6e6b7b;
        font-size: 0.75rem;
      }
    }
  }
}

// Enhance select styling
::v-deep .vs__dropdown-toggle {
  padding: 0.4rem;
  border-color: #d8d6de;

  &:hover {
    border-color: #7367f0;
  }
}

::v-deep .vs--open .vs__dropdown-toggle {
  border-color: #7367f0;
}

::v-deep .vs__search::placeholder {
  color: #b9b9c3;
}

::v-deep .vs__selected {
  color: #5e5873;
}

// Input focus states
.form-control:focus, .custom-select:focus {
  border-color: #7367f0;
}

.input-group-text {
  background-color: #f8f8f8;
  border-color: #d8d6de;
}

.input-group-merge .form-control:focus ~ .input-group-append .input-group-text {
  border-color: #7367f0;
}

// Enhanced v-select styling to ensure it shows properly
::v-deep .enhanced-select {
  .vs__dropdown-toggle {
    padding: 0.4rem;
    border: 1px solid #d8d6de;
    background-color: #fff;

    &:hover {
      border-color: #7367f0;
    }
  }

  .vs__selected {
    padding: 2px 6px;
    margin: 2px;
  }

  .vs__dropdown-menu {
    min-width: 100%;
    width: auto;
    padding: 0;
    border-radius: 0.357rem;
    box-shadow: 0 5px 25px rgba(0, 0, 0, 0.1);
  }
}
</style>
